import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
interface FAQItem {
  question: string;
  answer: string;
}
const FAQPage = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  const faqItems: FAQItem[] = [{
    question: 'How do I book driving lessons in Northampton?',
    answer: "You can book lessons directly through our Total Drive app once you've registered with us. The app allows you to choose available slots, check lesson gaps, and confirm bookings instantly."
  }, {
    question: 'Do you offer both manual and automatic driving lessons?',
    answer: 'No. We only provide automatic lessons at the moment.'
  }, {
    question: 'How much do driving lessons cost?',
    answer: 'Our pricing is competitive and affordable. We also offer block booking discounts for pupils who book multiple lessons in advance. Contact us for the latest rates.'
  }, {
    question: 'Can I pay for lessons online?',
    answer: 'Yes. You can pay securely through the Total Drive app using card payments. Your full payment history is available in the app for peace of mind.'
  }, {
    question: 'How do I track my progress?',
    answer: "Your instructor updates your Progress Syllabus in the app after each lesson. You can see how you're improving in key driving skills such as moving off, gear changes, and manoeuvres."
  }, {
    question: 'What if I need to cancel or reschedule a lesson?',
    answer: "You can manage bookings directly in the Total Drive app. Please give at least 24 hours' notice for cancellations or rescheduling to avoid being charged."
  }, {
    question: 'Do you provide intensive driving courses?',
    answer: 'Yes. We offer intensive driving courses in Northampton for pupils who want to learn quickly. These are subject to availability – ask us for details.'
  }, {
    question: 'Can I book my theory or practical test through you?',
    answer: 'Yes. Through the Resources tab in the Total Drive app, you can book your DVSA theory test and practical driving test directly with the DVSA.'
  }, {
    question: 'What is a Reflective Log?',
    answer: "After each lesson, you'll be asked to complete a Reflective Log in the app. This helps you review what went well, what to improve, and set goals for the next lesson."
  }, {
    question: 'Do you offer refresher lessons?',
    answer: 'Yes. We provide refresher driving lessons for drivers who want to rebuild confidence or update their driving skills.'
  }, {
    question: 'What areas do you cover?',
    answer: "We are based in Northampton and cover the local area and surrounding towns. If you're unsure whether we cover your area, please contact us."
  }, {
    question: 'How many lessons will I need to pass my test?',
    answer: 'The number of lessons varies from person to person. The DVSA suggests that, on average, it takes around 45 hours of professional instruction plus 22 hours of private practice to reach test standard. However, this can vary based on your learning pace, previous experience, and how frequently you take lessons. During your first lesson, your instructor will assess your current ability and provide a more personalised estimate.'
  }, {
    question: 'What are the advantages of learning in an automatic car?',
    answer: 'Learning in an automatic car can make the process easier and less stressful for many learners. Without the need to master clutch control and gear changes, you can focus more on road awareness, hazard perception, and other essential driving skills. This often leads to faster progress and increased confidence, especially for those who find the coordination required for manual driving challenging.'
  }, {
    question: 'Are your instructors qualified and DBS checked?',
    answer: 'Yes, all our instructors are fully qualified Approved Driving Instructors (ADIs) registered with the DVSA. They undergo regular training to stay updated with the latest teaching methods and driving standards. Additionally, all instructors have current DBS (Disclosure and Barring Service) checks for your peace of mind.'
  }, {
    question: 'Do you offer theory test preparation?',
    answer: 'Yes, we provide theory test preparation as part of our lessons. Your instructor will cover relevant theory topics during practical lessons and can recommend resources for additional study. We also have a dedicated Tips page on our website with advice for passing your theory test.'
  }, {
    question: "What if I've had lessons before?",
    answer: "If you've had previous driving experience, we'll assess your current skill level during your first lesson and create a personalised plan to build on what you already know. This ensures we don't waste time covering skills you've already mastered."
  }];
  return <div className="bg-white" data-id="element-1343">
      <Helmet data-id="element-1344">
        <title data-id="element-1345">
          Driving Lessons Northampton FAQ | Northampton Driving School
        </title>
        <meta name="description" content="Find answers about lesson costs, booking through the Total Drive app, and course options. Everything you need to start your lessons in Northampton." data-id="element-1346" />
        <meta name="keywords" content="driving lessons FAQ Northampton, automatic driving lessons questions, driving instructor Northampton, Total Drive app help" data-id="element-1347" />
      </Helmet>
      <div className="container mx-auto px-4 py-12" data-id="element-1348">
        <h1 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1349">
          Frequently Asked Questions
        </h1>
        <h2 className="sr-only" data-id="element-1350">Automatic Driving Lessons FAQ Northampton</h2>
        <h2 className="sr-only" data-id="element-1351">Driving Instructor Questions and Answers</h2>
        <p className="text-gray-700 mb-8" data-id="element-1352">
          Find answers to common questions about our driving lessons, booking
          process, and more. If you can't find the information you're looking
          for, please don't hesitate to{' '}
          <Link to="/contact" className="text-burgundy-600 hover:text-burgundy-800" aria-label="Contact us to book your driving lessons in Northampton" data-id="element-1353">
            book your driving lessons in Northampton
          </Link>{' '}
          or contact us directly.
        </p>
        <div className="space-y-4 mb-8" data-id="element-1354">
          {faqItems.map((item, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1355">
              <button className="flex justify-between items-center w-full px-6 py-4 text-left font-medium focus:outline-none" onClick={() => toggleFAQ(index)} aria-expanded={activeIndex === index} aria-controls={`faq-answer-${index}`} data-id="element-1356">
                <span data-id="element-1357">{item.question}</span>
                {activeIndex === index ? <ChevronUp size={20} className="text-burgundy-600" aria-hidden="true" data-id="element-1358" /> : <ChevronDown size={20} className="text-burgundy-600" aria-hidden="true" data-id="element-1359" />}
              </button>
              {activeIndex === index && <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" id={`faq-answer-${index}`} data-id="element-1360">
                  <p className="text-gray-700" data-id="element-1361">{item.answer}</p>
                </div>}
            </div>)}
        </div>
        <div className="bg-burgundy-50 border border-burgundy-200 rounded-lg p-6" data-id="element-1362">
          <h2 className="text-xl font-bold mb-4" data-id="element-1363">Still Have Questions?</h2>
          <p className="mb-4" data-id="element-1364">
            We're here to help! If you couldn't find the answer to your
            question, please don't hesitate to get in touch with us.
          </p>
          <a href="https://www.totaldrive.app/a/enquiry_form.php?17584722727972" target="_blank" rel="noopener noreferrer" className="bg-burgundy-600 hover:bg-burgundy-700 text-white px-6 py-3 rounded-md font-medium inline-block transition-colors" aria-label="Contact us about driving lessons in Northampton" data-id="element-1365">
            Contact Us
          </a>
        </div>
      </div>
    </div>;
};
export default FAQPage;